/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, no-prototype-builtins, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import ContactType from 'linced-app/enums/contact-type';
import MedicalRelationship from '@linc-technologies/linced-common/enums/medical-relationship';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ContactMapper extends Component {
  // Services
  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  ContactType = ContactType;
  MedicalRelationship = MedicalRelationship;
  skipped = false;
  @tracked
  compareDialogOpen = false;
  @tracked
  medicalContactsDialogOpen = false;
  @tracked
  medicalContactInProgress = false;
  caregiverFields = [];
  medicalFields = [];
  relationshipFields = [];
  medicalRelationshipFields = [];

  @tracked
  compareContact;

  @tracked
  medicalContact;

  @computed('medicalContact')
  get medicalContactName() {
    if (isEmpty(this.medicalContact)) {
      return '';
    }
    let firstName = this.get('medicalContact.data.firstName');
    let lastName = this.get('medicalContact.data.lastName');
    return `${firstName} ${lastName}`.trim();
  }

  @computed('medicalContact', 'medicalContactName')
  get medicalCentreName() {
    if (isEmpty(this.medicalContact)) {
      return '';
    }
    let medicalCentreName = this.get(
      'medicalContact.data.fields.core:medicalCentre.valueString',
    );
    if (isEmpty(this.medicalContactName)) {
      return medicalCentreName;
    }
    return `(${medicalCentreName})`;
  }

  @action
  handleCompareContact(contact) {
    this.compareContact = contact;
    this.compareDialogOpen = true;
  }

  @action
  closeCompareDialog() {
    this.compareDialogOpen = false;
  }

  @action
  addContact(contact, updates) {
    if (contact === this.medicalContact) {
      return this.addMedicalContact(contact, updates);
    }
    let record = findBy(
      this.person.learner.contacts,
      'caregiverId',
      contact.result.personId,
    );
    if (isEmpty(record)) {
      // Add contact to learner
      this.person.learner.contacts.createFragment({
        type: contact.data.type,
        caregiverId: contact.result.personId,
        relationship: contact.data.relationship,
        notes: contact.data.notes,
        billPayer: contact.data.billPayer,
        priority: contact.priority,
      });
      record = findBy(
        this.person.learner.contacts,
        'caregiverId',
        contact.result.personId,
      );
    } else if (record.type === contact.data.type) {
      // Contact already exists, and contact type matches.  Update details.
      record.setProperties({
        relationship: contact.data.relationship,
        notes: contact.data.notes,
        billPayer: contact.data.billPayer,
        priority: contact.priority,
      });
    } else {
      // Contact exists, but type is different.  Skip.
      return;
    }

    // Update contact relationship fields.
    this.populateContactFields(record, contact.data.fields);

    // Process updates from enrolment form if requested.
    let contactPromise = Promise.resolve();
    if (!isEmpty(updates)) {
      updates.forEach((update) => {
        let field = contact.person.get(`fields.${update.key}`);
        if (isEmpty(field)) {
          let caregiverField = findBy(this.caregiverFields, 'key', update.key);
          if (isEmpty(caregiverField)) {
            // Caregiver field not found - we can't create it, as we don't know its datatype.
            return;
          }
          contact.person.addField(update.key, caregiverField.dataType);
          field = contact.person.get(`fields.${update.key}`);
        }
        field.set('value', update.application);
      });
      contactPromise = contact.person.save();
    } else if (contact.isDirty) {
      contactPromise = contact.person.save();
    }

    // Wait for caregiver person update to resolve if applicable.
    contactPromise.then(() => {
      // Mark contact as complete, save learner and close comparison dialog.
      this.person.save();
      set(contact, 'complete', true);
      this.compareDialogOpen = false;
    });
  }

  @action
  showMedicalContacts(contact) {
    this.medicalContactsDialogOpen = true;
    this.medicalContact = contact;
  }

  @action
  closeMedicalContactsDialog() {
    this.medicalContactsDialogOpen = false;
    this.medicalContact = null;
  }

  @action
  selectMedicalContactResult(result) {
    set(this.medicalContact, 'person', result.person);
    set(this.medicalContact, 'result', result);
    let differences = this.getContactDifferences(this.medicalContact);
    if (isEmpty(differences)) {
      // Medical contact's details match those on file.
      this.addMedicalContact(this.medicalContact, null);
    } else {
      // Medical contact's details differ; offer the comparison.
      this.compareContact = this.medicalContact;
      this.compareDialogOpen = true;
    }
  }

  @action
  handleCreateMedicalContact() {
    this.medicalContactInProgress = true;
    return this.schoolSchema
      .getPeopleSchema()
      .then((peopleSchema) => {
        return this.createMedicalContact(this.medicalContact, peopleSchema);
      })
      .then(() => {
        this.person.learner.medicalContacts.pushObject(
          this.medicalContact.fragment,
        );
        return this.person.save();
      })
      .then(() => {
        this.medicalContactsDialogOpen = false;
        this.medicalContactInProgress = false;
      });
  }

  populateContactFields(contact, data) {
    this.relationshipFields.forEach((field) => {
      if (data.hasOwnProperty(field.key)) {
        let contactField = contact.get(`fields.${field.key}`);
        if (isEmpty(contactField)) {
          contact.addField(field.key, field.dataType);
          contactField = contact.get(`fields.${field.key}`);
        }
        contactField.set('value', data.get(`${field.key}.value`));
      }
    });
  }

  @action
  getContactDifferences(contact) {
    let isMedical = contact === this.medicalContact;
    let section = 'emergencyContacts';
    let relationshipFields = this.relationshipFields;
    if (isMedical) {
      section = 'medicalContacts';
      relationshipFields = this.medicalRelationshipFields;
    } else if (contact.data.type === ContactType.PRIMARY) {
      section = 'primaryContacts';
    }
    let fields = this.schema.get(`${section}.keyedFields`);
    return compact(
      fields.map((field) => {
        let relationshipField = findBy(relationshipFields, 'key', field.key);
        if (!isEmpty(relationshipField)) {
          // This field relates to the relationship between contact and learner
          // - don't try to compare it.
          return;
        }
        let contactValue = contact.data.get(`fields.${field.key}.value`) ?? '';
        let personValue = contact.person.get(`fields.${field.key}.value`) ?? '';
        let isDifferent =
          contactValue.toString().toLowerCase().trim() !==
          personValue.toString().toLowerCase().trim();
        if (isDifferent) {
          return {
            key: field.key,
            label: field.label,
            application: contactValue,
            existing: personValue,
          };
        }
      }),
    );
  }

  addMedicalContact(contact, updates) {
    let record = findBy(
      this.person.learner.medicalContacts,
      'medicalContactId',
      contact.result.personId,
    );
    if (isEmpty(record)) {
      // Add medical contact to learner.
      this.person.learner.medicalContacts.createFragment({
        medicalContactId: contact.result.personId,
        relationship: contact.data.relationship,
        notes: contact.data.notes,
        priority: contact.priority,
        permissionToContact: contact.permissionToContact,
      });
      record = findBy(
        this.person.learner.medicalContacts,
        'medicalContactId',
        contact.result.personId,
      );
    } else {
      // Contact already exists, and contact type matches.  Update details.
      record.setProperties({
        relationship: contact.data.relationship,
        notes: contact.data.notes,
        priority: contact.priority,
        permissionToContact: contact.permissionToContact,
      });
    }

    // Update contact relationship fields.
    this.populateContactFields(record, contact.data.fields);

    // Process updates from enrolment form if requested.
    let contactPromise = Promise.resolve();
    if (!isEmpty(updates)) {
      updates.forEach((update) => {
        let field = contact.person.get(`fields.${update.key}`);
        if (isEmpty(field)) {
          let medicalContactField = findBy(
            this.medicalFields,
            'key',
            update.key,
          );
          if (isEmpty(medicalContactField)) {
            // Medical contact field not found - we can't create it, as we
            // don't know its datatype.
            return;
          }
          contact.person.addField(update.key, medicalContactField.dataType);
          field = contact.person.get(`fields.${update.key}`);
        }
        field.set('value', update.application);
      });
      contactPromise = contact.person.save();
    } else if (contact.isDirty) {
      contactPromise = contact.person.save();
    }

    // Wait for medical contact person update to resolve if applicable.
    contactPromise.then(() => {
      // Mark contact as complete, save learner and close dialogs.
      this.person.save();
      set(contact, 'complete', true);
      this.compareDialogOpen = false;
      this.medicalContactsDialogOpen = false;
    });
  }
}
